import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Flexible from "@/components/ui/Flexible";
import { ArrowRight, Check, Zap, Building2, Server } from "lucide-react";
import FAQ from "@/components/ui/FAQ";
import PricingCards from "@/components/ui/PricingCards";
import CoreCapabilities from "@/components/ui/CoreCapabilities";
import CustomQuote from "@/components/ui/CustomQuote";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};
const faqData = [
  {
    question: "How is pricing calculated? ",
    answer:
      "Pricing is based on document processing volume, workflow complexity, and integration requirements. Our team will help determine the best plan based on your usage. ",
  },
  {
    question: "Do you offer custom enterprise pricing? ",
    answer:
      "Yes. Enterprise organizations can request custom pricing based on document volumes, security requirements, and deployment preferences. ",
  },
  {
    question: "Can Bridge Format AIQ scale with our business? ",
    answer:
      "Yes. The platform is designed to support growing document workloads, from small automation projects to large enterprise operations. ",
  },
  {
    question: "Is there a free trial available? ",
    answer:
      "Yes. Businesses can request a demo or trial environment to evaluate the platform before committing to a plan. ",
  },
  {
    question: "Can Bridge Format AIQ integrate with our existing systems?",
    answer:
      "Yes. Bridge Format AIQ is designed to integrate easily with your existing technology stack. Structured data can be exported to CRMs, ERPs, databases, analytics platforms, or other internal systems through APIs and integrations.",
  },
  {
    question: "Can pricing be customized for specific workflows?",
    answer:
      "Yes. Pricing can be adjusted depending on the document types, automation complexity, and integrations required for your workflows.",
  },
];
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const pricingPlans = [
  {
    icon: Zap,
    name: "Volume-Based",
    description:
      "Tiered pricing based on document processing volume with SLA-backed performance.",
    features: [
      "Pay-per-document pricing",
      "Tiered volume discounts",
      "Standard SLA (99.5%)",
      "API access",
      "Email support",
      "Standard templates",
    ],
    cta: "Get Started",
    highlight: false,
  },
  {
    icon: Building2,
    name: "Enterprise License",
    description:
      "Annual subscription with unlimited users, custom configuration, and dedicated support.",
    features: [
      "Unlimited document processing",
      "Custom format templates",
      "Premium SLA (99.9%)",
      "Full API access",
      "Dedicated support",
      "Custom integrations",
      "Training & onboarding",
      "Quarterly business reviews",
    ],
    cta: "Contact Sales",
    highlight: true,
  },
  {
    icon: Server,
    name: "Custom Deployment",
    description:
      "Private cloud or on-premise deployment for industry-specific governance requirements.",
    features: [
      "Private cloud or on-premise",
      "Custom security controls",
      "Data residency compliance",
      "Dedicated infrastructure",
      "White-glove onboarding",
      "Custom SLA options",
      "Source code escrow available",
    ],
    cta: "Request Proposal",
    highlight: false,
  },
];

export const Pricing = () => {
  return (
    <div className="pt-16 md:pt-20" data-testid="pricing-page">
      {/* Hero Section */}
      <section
        className="section-padding hero-texture min-h-[70vh] flex items-center"
        data-testid="pricing-hero"
      >
        <div className="container-width  relative z-10 vertical-center">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* <p className="label-mono mb-6">Pricing</p> */}

            <h1
              className="text-2xl md:text-5xl font-bold tracking-tight leading-[1.1] text-neutral-950 mb-8"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Flexible Pricing for AI-Powered Document Processing
            </h1>

            <p className="text-lg md:text-xl leading-relaxed text-neutral-600">
              Scale your document automation as your business grows. Bridge
              Format AIQ offers flexible usage-based pricing tailored to your
              workflow needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      {/* <section
        className="section-padding bg-neutral-50"
        data-testid="pricing-cards"
      >
        <div className="container-width">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {pricingPlans.map((plan) => (
              <motion.div
                key={plan.name}
                className={
                  plan.highlight ? "pricing-highlight" : "feature-card"
                }
                variants={fadeInUp}
              >
                <div className="flex items-center gap-3 mb-6">
                  <plan.icon
                    className={`w-6 h-6 ${
                      plan.highlight ? "text-white" : "text-neutral-950"
                    }`}
                  />
                  <span
                    className={`text-xs font-mono uppercase tracking-wider ${
                      plan.highlight ? "text-neutral-400" : "text-neutral-500"
                    }`}
                  >
                    {plan.name}
                  </span>
                </div>

                <h3
                  className={`text-xl font-semibold mb-4 ${
                    plan.highlight ? "text-white" : "text-neutral-950"
                  }`}
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {plan.name}
                </h3>

                <p
                  className={`text-sm leading-relaxed mb-8 ${
                    plan.highlight ? "text-neutral-300" : "text-neutral-600"
                  }`}
                >
                  {plan.description}
                </p>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          plan.highlight
                            ? "text-neutral-400"
                            : "text-neutral-950"
                        }`}
                      />
                      <span
                        className={`text-sm leading-relaxed ${
                          plan.highlight
                            ? "text-neutral-300"
                            : "text-neutral-600"
                        }`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/demo"
                  className={`w-full gap-2 btn-hover-scale ${
                    plan.highlight ? "btn-secondary" : "btn-primary"
                  }`}
                  data-testid={`pricing-${plan.name
                    .toLowerCase()
                    .replace(" ", "-")}-btn`}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* Pricing Cards */}
      <PricingCards />

      {/* Flexible Pricing */}
      <Flexible />

      {/* core capabilities */}

      <CoreCapabilities />

      {/* CTA Section */}

      <CustomQuote />

      {/* FAQ Section */}
      {/* <section className="section-padding" data-testid="pricing-faq">
        <div className="container-width">
          <motion.div
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-mono mb-6 text-center">Common Questions</p>

            <h2
              className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-12 text-center"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Pricing FAQ
            </h2>

            <div className="space-y-8">
              {[
                {
                  q: "How is document volume calculated?",
                  a: "Document volume is calculated based on the number of documents processed through the platform. Multi-page documents count as a single document.",
                },
                {
                  q: "What happens if I exceed my volume tier?",
                  a: "You can continue processing documents at the next tier rate. We will notify you when approaching tier limits so you can plan accordingly.",
                },
                {
                  q: "Is there a free trial available?",
                  a: "We offer a proof-of-concept period for enterprise customers. Contact our sales team to discuss your specific requirements.",
                },
                {
                  q: "What support is included?",
                  a: "All plans include email support. Enterprise and Custom Deployment plans include dedicated support with guaranteed response times.",
                },
              ].map((item, index) => (
                <div key={index} className="border-b border-neutral-200 pb-8">
                  <h4 className="text-base font-semibold text-neutral-950 mb-3">
                    {item.q}
                  </h4>
                  <p className="text-sm text-neutral-600 leading-relaxed">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section> */}
      <FAQ data={faqData} />

      {/* <section
        className="section-padding bg-neutral-950 text-white"
        data-testid="pricing-cta"
      >
        <div className="container-width">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="text-3xl md:text-5xl font-semibold tracking-tight mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Request a Pricing Proposal
            </h2>

            <p className="text-neutral-400 text-sm md:text-base mb-8 leading-relaxed">
              Tell us about your document processing needs and we will create a
              custom proposal for your organization.
            </p>

            <Link
              to="/demo"
              className="btn-secondary gap-2 btn-hover-scale"
              data-testid="pricing-proposal-btn"
            >
              Request Pricing Proposal
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default Pricing;
